var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useEffect, useRef } from 'react';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Polygon from '@arcgis/core/geometry/Polygon';
import Graphic from '@arcgis/core/Graphic';
import SketchViewModel from '@arcgis/core/widgets/Sketch/SketchViewModel';
import { assertGeographic, geographicPolygon, isPolygon, toMercator } from '../constants';
export function useMapEditing({ mapView, editMode, selectedId, areas, onGeometryChange, boundaryArea, }) {
    const editGraphicsLayerRef = useRef(null);
    const boundaryLayerRef = useRef(null);
    const sketchViewModelRef = useRef(null);
    const isActivelyEditingRef = useRef(false);
    const lastGeometryJsonRef = useRef('');
    const debounceTimerRef = useRef(null);
    // Initialize editing layers and sketch view model
    useEffect(() => {
        const view = mapView;
        if (!view || !view.map)
            return;
        if (!editGraphicsLayerRef.current) {
            editGraphicsLayerRef.current = new GraphicsLayer();
            view.map.add(editGraphicsLayerRef.current);
        }
        if (!boundaryLayerRef.current) {
            boundaryLayerRef.current = new GraphicsLayer();
            view.map.add(boundaryLayerRef.current);
        }
        view.when(() => {
            const sketchViewModel = new SketchViewModel({
                view,
                layer: editGraphicsLayerRef.current,
                updateOnGraphicClick: false,
                defaultUpdateOptions: {
                    tool: "reshape"
                }
            });
            sketchViewModelRef.current = sketchViewModel;
            // Handle geometry updates
            sketchViewModel.on("update", (event) => {
                var _a;
                try {
                    if (event.state === "active") {
                        isActivelyEditingRef.current = true;
                        const toolType = (_a = event.toolEventInfo) === null || _a === void 0 ? void 0 : _a.type;
                        if (toolType === 'reshape-stop' || toolType === 'move-stop' || toolType === 'scale-stop' || toolType === 'rotate-stop') {
                            if (event.graphics.length > 0) {
                                const graphic = event.graphics[0];
                                const areaId = (graphic === null || graphic === void 0 ? void 0 : graphic.getAttribute) ? graphic.getAttribute('areaId') : null;
                                if (areaId && isPolygon(graphic.geometry)) {
                                    const polygon = assertGeographic(graphic.geometry);
                                    if (polygon.rings && polygon.rings.length > 0) {
                                        const rings = polygon.rings.map(ring => ring.map(coord => [coord[0], coord[1]]));
                                        const multiPolygon = [rings];
                                        const geometryJson = JSON.stringify(multiPolygon);
                                        if (geometryJson !== lastGeometryJsonRef.current) {
                                            lastGeometryJsonRef.current = geometryJson;
                                            if (debounceTimerRef.current) {
                                                clearTimeout(debounceTimerRef.current);
                                            }
                                            debounceTimerRef.current = setTimeout(() => {
                                                if (onGeometryChange) {
                                                    onGeometryChange(areaId, multiPolygon);
                                                }
                                            }, 100);
                                        }
                                    }
                                }
                            }
                        }
                    }
                    if (event.state === "complete") {
                        isActivelyEditingRef.current = false;
                        const editGraphicsLayer = editGraphicsLayerRef.current;
                        if (editGraphicsLayer && event.graphics.length > 0) {
                            setTimeout(() => {
                                try {
                                    const graphics = editGraphicsLayer.graphics.toArray();
                                    if (graphics.length > 0) {
                                        sketchViewModel.update(graphics, {
                                            tool: "reshape",
                                            enableRotation: false,
                                            enableScaling: false,
                                            toggleToolOnClick: false
                                        });
                                    }
                                }
                                catch (_a) {
                                    // Ignore
                                }
                            }, 100);
                        }
                    }
                }
                catch (error) {
                    console.error("Error during geometry update:", error);
                }
            });
        });
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
            if (sketchViewModelRef.current) {
                sketchViewModelRef.current.destroy();
            }
        };
    }, [mapView]);
    // Display boundary area (for parcel mode)
    useEffect(() => {
        const boundaryLayer = boundaryLayerRef.current;
        const view = mapView;
        if (!boundaryLayer)
            return;
        if (editMode !== 'parcels') {
            boundaryLayer.removeAll();
            return;
        }
        boundaryLayer.removeAll();
        if (boundaryArea) {
            const { geometry } = boundaryArea;
            geometry.forEach((polygonRings) => {
                var _a;
                const rings = Array.isArray((_a = polygonRings === null || polygonRings === void 0 ? void 0 : polygonRings[0]) === null || _a === void 0 ? void 0 : _a[0]) ? polygonRings : [polygonRings];
                const polygon = geographicPolygon(rings);
                const boundaryGraphic = new Graphic({
                    geometry: polygon,
                    symbol: {
                        type: "simple-fill",
                        color: [0, 0, 0, 0],
                        outline: {
                            color: [0, 100, 200],
                            width: 3,
                            style: "solid"
                        },
                    },
                });
                boundaryLayer.add(boundaryGraphic);
            });
            if (view) {
                view.when(() => {
                    const graphics = boundaryLayer.graphics.toArray();
                    if (graphics.length > 0) {
                        view.goTo(graphics, {
                            duration: 1000,
                            easing: "ease-in-out"
                        });
                    }
                });
            }
        }
    }, [boundaryArea, editMode, mapView]);
    // Handle selected area for editing
    useEffect(() => {
        const editGraphicsLayer = editGraphicsLayerRef.current;
        const sketchViewModel = sketchViewModelRef.current;
        const view = mapView;
        if (!editGraphicsLayer || !view || !areas)
            return;
        // Don't enable polygon editing in parcel mode
        if (editMode === 'parcels') {
            editGraphicsLayer.removeAll();
            if (sketchViewModel) {
                try {
                    sketchViewModel.cancel();
                }
                catch (_a) {
                    // Ignore
                }
            }
            return;
        }
        // If selectedId is null (deselected/discarded), clear the edit layer
        if (!selectedId) {
            isActivelyEditingRef.current = false;
            editGraphicsLayer.removeAll();
            if (sketchViewModel) {
                try {
                    sketchViewModel.cancel();
                }
                catch (_b) {
                    // Ignore
                }
            }
            return;
        }
        if (isActivelyEditingRef.current) {
            return;
        }
        view.when(() => {
            editGraphicsLayer.removeAll();
            if (sketchViewModel) {
                try {
                    sketchViewModel.cancel();
                }
                catch (_a) {
                    // Ignore
                }
            }
            if (selectedId) {
                const selectedArea = areas.find(d => d.id === selectedId);
                if (selectedArea) {
                    const { id, title, geometry } = selectedArea;
                    geometry.forEach(polygonRings => {
                        var _a;
                        const rings = Array.isArray((_a = polygonRings === null || polygonRings === void 0 ? void 0 : polygonRings[0]) === null || _a === void 0 ? void 0 : _a[0]) ? polygonRings : [polygonRings];
                        const geoPolygon = geographicPolygon(rings);
                        const polygon = view.spatialReference.wkid !== 4326 ? toMercator(geoPolygon) : geoPolygon;
                        const editGraphic = new Graphic({
                            geometry: polygon,
                            attributes: {
                                title,
                                areaId: id,
                            },
                            symbol: {
                                type: "simple-fill",
                                color: [0, 150, 255, 0.15],
                                outline: {
                                    color: [0, 150, 255],
                                    width: 3,
                                },
                            },
                        });
                        editGraphicsLayer.add(editGraphic);
                    });
                    const graphics = editGraphicsLayer.graphics.toArray();
                    if (graphics.length > 0 && sketchViewModel) {
                        view.goTo(graphics, {
                            duration: 1000,
                            easing: "ease-in-out"
                        }).catch(() => {
                            // Ignore animation errors
                        }).then(() => {
                            setTimeout(() => __awaiter(this, void 0, void 0, function* () {
                                try {
                                    yield sketchViewModel.update(graphics[0], {
                                        tool: "reshape"
                                    });
                                }
                                catch (e) {
                                    console.error("Error starting sketch edit:", e);
                                }
                            }), 500);
                        });
                    }
                }
            }
        });
    }, [selectedId, areas, editMode, mapView]);
    // Editing helper functions
    const handleSubdivide = () => {
        const editGraphicsLayer = editGraphicsLayerRef.current;
        const sketchViewModel = sketchViewModelRef.current;
        const view = mapView;
        if (!editGraphicsLayer || !sketchViewModel || !view || !selectedId)
            return;
        const graphics = editGraphicsLayer.graphics.toArray();
        if (graphics.length === 0)
            return;
        const graphic = graphics[0];
        if (!isPolygon(graphic.geometry))
            return;
        const polygon = graphic.geometry;
        const areaId = graphic.getAttribute('areaId');
        const newRings = polygon.rings.map(ring => {
            const newRing = [];
            for (let i = 0; i < ring.length - 1; i++) {
                const current = ring[i];
                const next = ring[i + 1];
                newRing.push(current);
                const midX = (current[0] + next[0]) / 2;
                const midY = (current[1] + next[1]) / 2;
                newRing.push([midX, midY]);
            }
            newRing.push(ring[ring.length - 1]);
            return newRing;
        });
        const newPolygon = new Polygon({
            rings: newRings,
            spatialReference: polygon.spatialReference
        });
        // Mark as actively editing to prevent the effect from re-running
        isActivelyEditingRef.current = true;
        graphic.geometry = newPolygon;
        if (sketchViewModel.updateGraphics && sketchViewModel.updateGraphics.length > 0) {
            const index = editGraphicsLayer.graphics.indexOf(graphic);
            if (index !== -1) {
                editGraphicsLayer.graphics.removeAt(index);
                editGraphicsLayer.graphics.add(graphic, index);
            }
        }
        // Reset the flag after a brief delay
        setTimeout(() => {
            isActivelyEditingRef.current = false;
        }, 100);
        const geoPolygon = assertGeographic(newPolygon);
        if (geoPolygon.rings && geoPolygon.rings.length > 0 && onGeometryChange && areaId) {
            const rings = geoPolygon.rings.map(ring => ring.map(coord => [coord[0], coord[1]]));
            const multiPolygon = [rings];
            onGeometryChange(areaId, multiPolygon);
        }
    };
    const simplifyRing = (ring, tolerance) => {
        if (ring.length <= 4)
            return ring;
        const perpendicularDistance = (point, lineStart, lineEnd) => {
            const dx = lineEnd[0] - lineStart[0];
            const dy = lineEnd[1] - lineStart[1];
            const lineLengthSquared = dx * dx + dy * dy;
            if (lineLengthSquared === 0) {
                return Math.sqrt(Math.pow((point[0] - lineStart[0]), 2) + Math.pow((point[1] - lineStart[1]), 2));
            }
            const t = Math.max(0, Math.min(1, ((point[0] - lineStart[0]) * dx + (point[1] - lineStart[1]) * dy) / lineLengthSquared));
            const projX = lineStart[0] + t * dx;
            const projY = lineStart[1] + t * dy;
            return Math.sqrt(Math.pow((point[0] - projX), 2) + Math.pow((point[1] - projY), 2));
        };
        const douglasPeucker = (points, startIdx, endIdx, tolerance, keepIndices) => {
            if (endIdx <= startIdx + 1)
                return;
            let maxDist = 0;
            let maxIdx = startIdx;
            for (let i = startIdx + 1; i < endIdx; i++) {
                const dist = perpendicularDistance(points[i], points[startIdx], points[endIdx]);
                if (dist > maxDist) {
                    maxDist = dist;
                    maxIdx = i;
                }
            }
            if (maxDist > tolerance) {
                keepIndices.add(maxIdx);
                douglasPeucker(points, startIdx, maxIdx, tolerance, keepIndices);
                douglasPeucker(points, maxIdx, endIdx, tolerance, keepIndices);
            }
        };
        const openRing = ring.slice(0, -1);
        const keepIndices = new Set([0, openRing.length - 1]);
        douglasPeucker(openRing, 0, openRing.length - 1, tolerance, keepIndices);
        const simplified = [];
        for (let i = 0; i < openRing.length; i++) {
            if (keepIndices.has(i)) {
                simplified.push(openRing[i]);
            }
        }
        simplified.push(simplified[0]);
        if (simplified.length < 4)
            return ring;
        return simplified;
    };
    const handleUnsubdivide = () => {
        const editGraphicsLayer = editGraphicsLayerRef.current;
        const sketchViewModel = sketchViewModelRef.current;
        const view = mapView;
        if (!editGraphicsLayer || !sketchViewModel || !view || !selectedId)
            return;
        const graphics = editGraphicsLayer.graphics.toArray();
        if (graphics.length === 0)
            return;
        const graphic = graphics[0];
        if (!isPolygon(graphic.geometry))
            return;
        const polygon = graphic.geometry;
        const areaId = graphic.getAttribute('areaId');
        const extent = polygon.extent;
        if (!extent)
            return;
        const tolerance = Math.max(extent.width, extent.height) * 0.01;
        const newRings = polygon.rings.map(ring => simplifyRing(ring, tolerance));
        const newPolygon = new Polygon({
            rings: newRings,
            spatialReference: polygon.spatialReference
        });
        // Mark as actively editing to prevent the effect from re-running
        isActivelyEditingRef.current = true;
        // Update the geometry directly
        graphic.geometry = newPolygon;
        if (sketchViewModel.updateGraphics && sketchViewModel.updateGraphics.length > 0) {
            const index = editGraphicsLayer.graphics.indexOf(graphic);
            if (index !== -1) {
                editGraphicsLayer.graphics.removeAt(index);
                editGraphicsLayer.graphics.add(graphic, index);
            }
        }
        // Reset the flag after a brief delay
        setTimeout(() => {
            isActivelyEditingRef.current = false;
        }, 100);
        const geoPolygon = assertGeographic(newPolygon);
        if (geoPolygon.rings && geoPolygon.rings.length > 0 && onGeometryChange && areaId) {
            const rings = geoPolygon.rings.map(ring => ring.map(coord => [coord[0], coord[1]]));
            const multiPolygon = [rings];
            onGeometryChange(areaId, multiPolygon);
        }
    };
    const handleDelete = () => {
        const editGraphicsLayer = editGraphicsLayerRef.current;
        const sketchViewModel = sketchViewModelRef.current;
        if (!editGraphicsLayer || !sketchViewModel || !selectedId)
            return;
        try {
            sketchViewModel.cancel();
        }
        catch (_a) {
            // Ignore
        }
        editGraphicsLayer.removeAll();
        if (onGeometryChange) {
            onGeometryChange(selectedId, []);
        }
    };
    const handleAddRectangle = () => {
        const editGraphicsLayer = editGraphicsLayerRef.current;
        const sketchViewModel = sketchViewModelRef.current;
        const view = mapView;
        if (!editGraphicsLayer || !sketchViewModel || !view || !selectedId)
            return;
        const center = view.center;
        const extent = view.extent;
        if (!center || !extent)
            return;
        const width = (extent.xmax - extent.xmin) * 0.25;
        const height = (extent.ymax - extent.ymin) * 0.25;
        const rings = [[
                [center.x - width, center.y - height],
                [center.x + width, center.y - height],
                [center.x + width, center.y + height],
                [center.x - width, center.y + height],
                [center.x - width, center.y - height]
            ]];
        const newPolygon = new Polygon({
            rings,
            spatialReference: view.spatialReference
        });
        const areaId = selectedId;
        editGraphicsLayer.removeAll();
        const newGraphic = new Graphic({
            geometry: newPolygon,
            attributes: {
                areaId,
                title: 'New Rectangle'
            },
            symbol: {
                type: "simple-fill",
                color: [255, 165, 0, 0.25],
                outline: {
                    color: [255, 140, 0],
                    width: 3,
                },
            },
        });
        editGraphicsLayer.add(newGraphic);
        setTimeout(() => {
            try {
                sketchViewModel.update([newGraphic], {
                    tool: "reshape",
                    enableRotation: false,
                    enableScaling: false,
                    toggleToolOnClick: false
                });
            }
            catch (e) {
                console.error("Error starting edit on new rectangle:", e);
            }
        }, 50);
        const geoPolygon = assertGeographic(newPolygon);
        if (geoPolygon.rings && geoPolygon.rings.length > 0 && onGeometryChange) {
            const geoRings = geoPolygon.rings.map(ring => ring.map(coord => [coord[0], coord[1]]));
            const multiPolygon = [geoRings];
            onGeometryChange(areaId, multiPolygon);
        }
    };
    const handleRotate = (degrees) => {
        const editGraphicsLayer = editGraphicsLayerRef.current;
        const sketchViewModel = sketchViewModelRef.current;
        if (!editGraphicsLayer || !sketchViewModel || !selectedId)
            return;
        const graphics = editGraphicsLayer.graphics.toArray();
        if (graphics.length === 0)
            return;
        const graphic = graphics[0];
        if (!isPolygon(graphic.geometry))
            return;
        const polygon = graphic.geometry;
        const areaId = graphic.getAttribute('areaId');
        const centroid = polygon.centroid;
        if (!centroid)
            return;
        const radians = (degrees * Math.PI) / 180;
        const newRings = polygon.rings.map(ring => {
            return ring.map(coord => {
                const x = coord[0] - centroid.x;
                const y = coord[1] - centroid.y;
                const newX = x * Math.cos(radians) - y * Math.sin(radians);
                const newY = x * Math.sin(radians) + y * Math.cos(radians);
                return [newX + centroid.x, newY + centroid.y];
            });
        });
        const newPolygon = new Polygon({
            rings: newRings,
            spatialReference: polygon.spatialReference
        });
        // Mark as actively editing to prevent the effect from re-running
        isActivelyEditingRef.current = true;
        // Update the geometry directly
        graphic.geometry = newPolygon;
        // The graphic is already being tracked by the SketchViewModel
        // We just need to trigger a refresh by accessing the updateGraphics
        if (sketchViewModel.updateGraphics && sketchViewModel.updateGraphics.length > 0) {
            // The handles should update automatically since we modified the geometry
            // Force a re-render by touching the layer
            const index = editGraphicsLayer.graphics.indexOf(graphic);
            if (index !== -1) {
                editGraphicsLayer.graphics.removeAt(index);
                editGraphicsLayer.graphics.add(graphic, index);
            }
        }
        // Reset the flag after a brief delay
        setTimeout(() => {
            isActivelyEditingRef.current = false;
        }, 100);
        const geoPolygon = assertGeographic(newPolygon);
        if (geoPolygon.rings && geoPolygon.rings.length > 0 && onGeometryChange && areaId) {
            const rings = geoPolygon.rings.map(ring => ring.map(coord => [coord[0], coord[1]]));
            const multiPolygon = [rings];
            onGeometryChange(areaId, multiPolygon);
        }
    };
    return {
        handleSubdivide,
        handleUnsubdivide,
        handleDelete,
        handleAddRectangle,
        handleRotate,
    };
}
